import { useMemo, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUploadIndicatorBatches } from 'store/storeTransactionImage/storeTransactionImageSelector';
import UploadingStatusImage from 'assets/icons/UploadStatus/UploadingStatusImage';
import SavedStatusImage from 'assets/icons/UploadStatus/SavedStatusImage';
import FailedStatusImage from 'assets/icons/UploadStatus/FailedStatusImage';
import { CircularProgress, Box, Tooltip } from '@mui/material';
import UploadNotification from '../UploadNotification/UploadNotification';
import styles from './UploadStatusIndicator.module.css';

const UploadStatusIndicator = () => {
    // agent snapshots uploaded by the browser + Macaw images uploaded by the backend
    const batches = useSelector(selectUploadIndicatorBatches);
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);

    const uploadStatus = useMemo(() => {
        if (!batches || batches.length === 0) {
            return { status: 'idle', total: 0, saved: 0, failed: 0, uploading: 0 };
        }

        let total = 0;
        let saved = 0;
        let failed = 0;
        let uploading = 0;

        batches.forEach((batch) => {
            batch.images.forEach((img) => {
                total++;
                if (img.status === 'saved') saved++;
                else if (img.status === 'failed') failed++;
                else if (img.status === 'uploading' || img.status === 'inFlight') uploading++;
            });
        });

        let status = 'idle';
        if (uploading > 0) status = 'uploading';
        else if (failed > 0) status = 'failed';
        else if (saved > 0) status = 'saved';

        return { status, total, saved, failed, uploading };
    }, [batches]);

    const handleToggle = () => {
        setIsOpen((prev) => !prev);
    };

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const getTooltipText = () => {
        if (uploadStatus.uploading > 0) {
            return `Uploading ...`;
        }
        if (uploadStatus.failed > 0) {
            return `${uploadStatus.failed} upload(s) failed.`;
        }
        return 'Everything is uploaded';
    };

    const renderIcon = () => {
        switch (uploadStatus.status) {
            case 'uploading':
                return (
                    <Box className={styles.iconWrapper}>
                        <UploadingStatusImage />
                        <Box className={styles.spinnerOverlay}>
                            <CircularProgress size={11} thickness={5} />
                        </Box>
                    </Box>
                );
            case 'failed':
                return <FailedStatusImage />;
            case 'saved':
            case 'idle':
            default:
                return <SavedStatusImage />;
        }
    };

    const isDefaultState = uploadStatus.uploading === 0 && uploadStatus.failed === 0;

    return (
        <>
            <Tooltip title={getTooltipText()} placement="left">
                <button
                    ref={buttonRef}
                    className={`${styles.indicator} ${isOpen ? styles.active : ''}`}
                    onClick={handleToggle}
                    aria-label="Toggle upload status"
                >
                    {renderIcon()}
                </button>
            </Tooltip>
            <UploadNotification isOpen={isOpen} defaultState={isDefaultState} onClose={handleClose} triggerRef={buttonRef} />
        </>
    );
};

export default UploadStatusIndicator;
